import {Box, Card, FAQ, FAQGroup, Grid, InlineLink, Section, Text} from '@primer/react-brand/esm'
import {ArrowUpIcon} from '@primer/octicons-react'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {renderWithoutParagraphs, renderWithSpans} from '../../../utils'
import type {OutroSectionProps} from './types'
import baseStyles from '../../../styles/base.module.css'
import overviewStyles from '../../OverviewTemplate.module.css'
import {BrandedDivider} from '../../../components/BrandedDivider'
import {FlexSuiteOutroCTA} from '../../../components/FlexSuiteOutroCTA'
import {FlexSuiteOutroResources} from '../../../components/FlexSuiteOutroResources'
import {clsx} from 'clsx'

export const OutroSection = ({
  settings,
  outroCustomerStories,
  outroCtaBanner,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroIncludeResources,
  outroResourcesHeading,
  outroResources,
  outroFaqs,
  outroHeading,
  footnotes,
}: OutroSectionProps) => {
  const brand = settings?.fields.brand
  const groupsWithFaqs = outroFaqs?.filter(group =>
    group.fields.faqs.some(faq => faq.fields.question && faq.fields.answer),
  )
  const singleGroup = groupsWithFaqs && groupsWithFaqs.length === 1 ? groupsWithFaqs[0] : null

  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0

  if (
    !hasCustomerStories &&
    !outroCtaBanner &&
    (!outroIncludeResources || !outroResources?.length) &&
    (!groupsWithFaqs || groupsWithFaqs.length === 0)
  ) {
    return null
  }

  const storySpan = outroCustomerStories && outroCustomerStories.length > 2 ? 4 : 6

  return (
    <Box marginBlockStart={48} className={baseStyles.greenBorderBottom}>
      <Section paddingBlockStart="none" className={baseStyles.gridFrameTop} paddingBlockEnd="none" fullWidth>
        {hasCustomerStories && (
          <Box className={`${baseStyles.gridFrameBottom}`}>
            <Grid columnGap="none" rowGap="none" enableGutters={false}>
              <Grid.Column span={{xsmall: 12, medium: 10, xlarge: 12}} start={{medium: 2}}>
                <Grid columnGap="none" rowGap="none" enableGutters={false} className={baseStyles.narrowStackFrame}>
                  {outroCustomerStories.map(story => {
                    const {logo, lead, url, title} = story.fields
                    return (
                      <Grid.Column
                        key={story.sys.id}
                        span={{xsmall: 12, xlarge: storySpan}}
                        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                        className="gridColumn"
                      >
                        <Box
                          className={clsx(baseStyles.gridItem, baseStyles.stackedCardGridItem)}
                          padding={{narrow: 24, regular: 40}}
                          paddingInlineStart={{narrow: 24, regular: 80, wide: 64}}
                          paddingInlineEnd={{narrow: 24, regular: 80, wide: 64}}
                          paddingBlockStart={{regular: 80}}
                          paddingBlockEnd={{regular: 80}}
                        >
                          <Card href={url} variant="minimal" fullWidth ctaVariant="arrow">
                            <Card.Image
                              src={`${logo.fields.file.url}?fm=webp&h=60`}
                              alt={logo.fields.description || title}
                              style={{height: '30px', width: 'auto', objectFit: 'contain'}}
                            />
                            <Card.Heading>{title}</Card.Heading>
                            <Card.Description>{lead}</Card.Description>
                          </Card>
                        </Box>
                      </Grid.Column>
                    )
                  })}
                </Grid>
              </Grid.Column>
            </Grid>
          </Box>
        )}

        {outroCtaBanner && (
          <FlexSuiteOutroCTA
            outroCtaHeading={outroCtaHeading}
            outroCtaDescription={outroCtaDescription}
            outroCtaOne={outroCtaOne}
            outroCtaTwo={outroCtaTwo}
          />
        )}

        {outroIncludeResources && (
          <FlexSuiteOutroResources outroResourcesHeading={outroResourcesHeading} outroResources={outroResources} />
        )}

        {brand === 'Security' ? (
          <Box className={baseStyles.gridFrameBottom}>
            <Grid>
              <Grid.Column>
                <BrandedDivider brand={brand} />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {groupsWithFaqs && groupsWithFaqs.length > 1 && (
          <FAQGroup variant="gridline" defaultSelectedIndex={0}>
            {outroHeading && (
              <FAQGroup.Heading>
                <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingInlineStart="spacious">
                  {renderWithSpans(outroHeading, 'd-block')}
                </Box>
              </FAQGroup.Heading>
            )}

            {groupsWithFaqs.map(group => {
              const completeFaqs = group.fields.faqs.filter(faq => faq.fields.question && faq.fields.answer)

              return (
                <FAQ key={group.sys.id} variant="gridline">
                  {group.fields.heading && <FAQ.Heading>{renderWithoutParagraphs(group.fields.heading)}</FAQ.Heading>}
                  {completeFaqs.map((faq, index) => (
                    <FAQ.Item key={faq.sys.id} name={`faq-${group.sys.id}`} open={index === 0}>
                      <FAQ.Question>{faq.fields.question}</FAQ.Question>
                      <FAQ.Answer>
                        <Text as="div" variant="muted">
                          {documentToReactComponents(faq.fields.answer)}
                        </Text>
                      </FAQ.Answer>
                    </FAQ.Item>
                  ))}
                </FAQ>
              )
            })}
          </FAQGroup>
        )}

        {singleGroup && (
          <FAQ key={singleGroup.sys.id} variant="gridline">
            {outroHeading && (
              <FAQ.Heading>
                <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingInlineStart="spacious">
                  {renderWithSpans(outroHeading, 'd-block')}
                </Box>
              </FAQ.Heading>
            )}
            {singleGroup.fields.heading && (
              <FAQ.Heading>{renderWithoutParagraphs(singleGroup.fields.heading)}</FAQ.Heading>
            )}
            {singleGroup.fields.faqs
              .filter(faq => faq.fields.question && faq.fields.answer)
              .map((faq, index) => (
                <FAQ.Item key={faq.sys.id} name={`faq-${singleGroup.sys.id}`} open={index === 0}>
                  <FAQ.Question>{faq.fields.question}</FAQ.Question>
                  <FAQ.Answer>
                    <Text as="div" variant="muted">
                      {documentToReactComponents(faq.fields.answer)}
                    </Text>
                  </FAQ.Answer>
                </FAQ.Item>
              ))}
          </FAQ>
        )}

        {footnotes && footnotes.length > 0 && (
          <Grid>
            <Grid.Column>
              <Box
                paddingBlockStart={{narrow: 'spacious', regular: 64}}
                paddingBlockEnd={{narrow: 'spacious', regular: 64}}
              >
                <Text as="div" size="100" variant="muted">
                  <ol className={overviewStyles.footnotesList}>
                    {footnotes.map((footnote, index) => (
                      <li key={footnote.id} id={`footnote-${index + 1}`} className={overviewStyles.footnotesItem}>
                        {renderWithoutParagraphs(footnote.content)}{' '}
                        <InlineLink
                          href="#pricing-section"
                          aria-label={`Back to pricing section (footnote ${index + 1})`}
                        >
                          <ArrowUpIcon size={12} />
                        </InlineLink>
                      </li>
                    ))}
                  </ol>
                </Text>
              </Box>
            </Grid.Column>
          </Grid>
        )}
      </Section>
    </Box>
  )
}
